import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
    CompareContractsInput,
    CompareContractsOutput,
    CreateNewContractInput,
    CreateNewContractOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';

import { isEqual, isUndefined } from 'lodash';
import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { CM_UI_SUPPORT_SERVICE_ID_LIST } from 'src/constants/context';
import { RateCardStatus } from 'src/constants/enums';
import { ALPHANUMERIC_ONLY, AT_MOST_249_CHAR, IS_REQUIRED, YEAR_MONTH_ONLY } from 'src/constants/errorMessages';
import { TEMPLATE_RATE_CARD_DESELECT_OPTION, defaultAddRateCardModalValues } from 'src/constants/rateCards';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IAddRateCardModalValues, IRateCardsRowData } from 'src/types/IRateCard';
import { buildRateCardName, getRateCardStatus } from 'src/util/RateCards';
import {
    validateAlphanumeric,
    validateMaxLength,
    validateRequired,
    validateYearMonthFormat
} from 'src/util/validationsUtil';

import { LoaderForBtn } from '../../ConfirmationModal';

interface IAddRateCardModalProps {
    isAddRateCardModalOpen: boolean;
    setIsAddRateCardModalOpen: Dispatch<SetStateAction<boolean>>;
    rateCardsRowData: IRateCardsRowData[];
    setTemplateContractId: Dispatch<SetStateAction<number | undefined>>;
    setNewContractId: Dispatch<SetStateAction<number | undefined>>;
}

export const AddRateCardModal = ({
    isAddRateCardModalOpen,
    setIsAddRateCardModalOpen,
    rateCardsRowData,
    setNewContractId,
    setTemplateContractId
}: IAddRateCardModalProps) => {
    const { metadata, setTopAlertMsg, userInfo } = React.useContext(AppContext);
    const history = useHistory();

    const [addRateCardModalValues, setAddRateCardModalValues] = useState(defaultAddRateCardModalValues);
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const [searchTemplateRateCardQuery, setSearchTemplateRateCardQuery] = useState('');
    const [isAddRateCardModalFormValid, setIsAddRateCardModalFormValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const services = useMemo(
        () =>
            Object.values(metadata.serviceMapping).filter(
                (service) => service.serviceId != undefined && CM_UI_SUPPORT_SERVICE_ID_LIST.includes(service.serviceId)
            ),
        []
    );

    const matchedRateCardOptions = useMemo(
        () =>
            TEMPLATE_RATE_CARD_DESELECT_OPTION.concat(
                rateCardsRowData.filter(
                    (option) =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        [RateCardStatus.Active, RateCardStatus.Inactive].includes(getRateCardStatus(option)) &&
                        (addRateCardModalValues.serviceId === '' ||
                            parseInt(addRateCardModalValues.serviceId) === option.serviceId) &&
                        (!searchTemplateRateCardQuery ||
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            option.contractName!.toLowerCase().includes(searchTemplateRateCardQuery.toLowerCase()))
                )
            ),
        [rateCardsRowData, searchTemplateRateCardQuery, addRateCardModalValues.serviceId]
    );

    useEffect(() => {
        setIsAddRateCardModalFormValid(checkAddRateCardModalValidity());
    }, [addRateCardModalValues, errorMessages]);

    const handleAddRateCardModalValuesChange = (key: string, value: string) => {
        setAddRateCardModalValues((prevState) => ({
            ...prevState,
            [key]: value
        }));
    };

    const handleCancelAddRateCard = () => {
        setIsAddRateCardModalOpen(false);
        setAddRateCardModalValues(defaultAddRateCardModalValues);
        setErrorMessages({});
        setSearchTemplateRateCardQuery('');
        setIsAddRateCardModalFormValid(false);
    };

    const getTemplateContractId = () => {
        const { templateRateCardId } = addRateCardModalValues;

        if (templateRateCardId === '') return undefined;

        const parsedId = parseInt(templateRateCardId as string);
        return isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;
    };

    const handleAddRateCardModalNext = () => {
        const templateContractId = getTemplateContractId();

        setTemplateContractId(templateContractId);

        setIsLoading(true);
        const createRateCardInput = CreateNewContractInput.clone({
            contractName: buildRateCardName(
                addRateCardModalValues,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                metadata.serviceMapping[parseInt(addRateCardModalValues.serviceId)].serviceName!
            ),
            templateContractId: templateContractId,
            serviceId: parseInt(addRateCardModalValues.serviceId),
            description: addRateCardModalValues.description,
            createdBy: userInfo.username
        });

        contractConfigurationClient.createNewContract(createRateCardInput).subscribe(
            (response: CreateNewContractOutput) => {
                if (!isUndefined(response.contractId)) {
                    setNewContractId(response.contractId);
                    if (isUndefined(templateContractId)) {
                        history.push(`${ROUTES.RATE_CARDS.INDEX}${ROUTES.RATE_CARDS.ADD}/${response.contractId}`);
                    }
                } else {
                    setTopAlertMsg('Contract creation returned undefined ID.');
                }
            },
            (error: UnknownCoralError) => {
                setTopAlertMsg(error.receivedException.message);
            },
            () => {
                setIsAddRateCardModalOpen(false);
                setIsLoading(false);
            }
        );
    };

    const onBlurAddRateCardModalValuesField = (field: keyof IAddRateCardModalValues) => {
        let errorMessage = '';
        const value = addRateCardModalValues[field];

        if (field === 'vendor') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (!validateAlphanumeric(value)) {
                errorMessage = ALPHANUMERIC_ONLY;
            }
        } else if (field === 'serviceId') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            }
        } else if (field === 'description') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (!validateMaxLength(value, 249)) {
                errorMessage = AT_MOST_249_CHAR;
            }
        } else if (field === 'month') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (!validateYearMonthFormat(value)) {
                errorMessage = YEAR_MONTH_ONLY;
            }
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                delete newState[field];
            } else {
                newState[field] = errorMessage;
            }
            return newState;
        });
    };

    const checkAddRateCardModalValidity = () => {
        const requiredFields = ['vendor', 'serviceId', 'description'];

        for (const field of requiredFields) {
            if (!validateRequired(addRateCardModalValues[field as keyof IAddRateCardModalValues])) {
                return false;
            }
        }

        return isEqual(errorMessages, {}) && !isEqual(addRateCardModalValues, defaultAddRateCardModalValues);
    };

    return (
        <Modal
            title="Create new contract"
            open={isAddRateCardModalOpen}
            onClose={handleCancelAddRateCard}
            scrollContainer="viewport"
            closeLabel="Close"
            width="600px"
        >
            <Column spacing="300">
                <Box type="fill" spacingInset="400">
                    <Text type="h400" transform="uppercase" alignment="center">
                        {buildRateCardName(
                            addRateCardModalValues,
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
                            metadata.serviceMapping[parseInt(addRateCardModalValues.serviceId)]?.serviceName!
                        )}
                    </Text>
                </Box>
                <Row widths={['70%', '30%']} alignmentVertical="top">
                    <Input
                        label="Vendor or vendor group *"
                        value={addRateCardModalValues.vendor}
                        onChange={(newVal) => handleAddRateCardModalValuesChange('vendor', newVal)}
                        onBlur={() => onBlurAddRateCardModalValuesField('vendor')}
                        error={!!errorMessages.vendor}
                        errorMessage={errorMessages.vendor}
                        size="xlarge"
                    />
                    <Input
                        label="YYYYMM *"
                        value={addRateCardModalValues.month}
                        onChange={(newVal) => handleAddRateCardModalValuesChange('month', newVal)}
                        onBlur={() => onBlurAddRateCardModalValuesField('month')}
                        error={!!errorMessages.month}
                        errorMessage={errorMessages.month}
                        size="xlarge"
                    />
                </Row>
                <Select
                    label="Service *"
                    value={addRateCardModalValues.serviceId}
                    onChange={(newVal) => handleAddRateCardModalValuesChange('serviceId', newVal)}
                    onBlur={() => onBlurAddRateCardModalValuesField('serviceId')}
                    error={!!errorMessages.serviceId}
                    errorMessage={errorMessages.serviceId}
                    size="xlarge"
                >
                    {services.map((service, index) => (
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        <SelectOption value={service.serviceId!} label={service.serviceName!} key={index} />
                    ))}
                </Select>
                <Input
                    label="Description *"
                    value={addRateCardModalValues.description}
                    onChange={(newVal) => handleAddRateCardModalValuesChange('description', newVal)}
                    onBlur={() => onBlurAddRateCardModalValuesField('description')}
                    error={!!errorMessages.description}
                    errorMessage={errorMessages.description}
                    size="xlarge"
                />
                <Select
                    label="Select a contract as template..."
                    value={addRateCardModalValues.templateRateCardId}
                    onChange={(newVal) => handleAddRateCardModalValuesChange('templateRateCardId', newVal ?? '')}
                    searchQuery={searchTemplateRateCardQuery}
                    onSearch={setSearchTemplateRateCardQuery}
                    size="xlarge"
                >
                    {!matchedRateCardOptions.length ? (
                        <Column alignmentVertical="center" spacing="300" spacingInset="600">
                            <Text alignment="center">No results</Text>
                        </Column>
                    ) : (
                        matchedRateCardOptions.map((rateCard, index) => (
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            <SelectOption value={rateCard.contractId!} label={rateCard.contractName!} key={index} />
                        ))
                    )}
                </Select>
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit">
                    <Button type="secondary" size="small" onClick={handleCancelAddRateCard}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        disabled={!isAddRateCardModalFormValid || isLoading}
                        onClick={handleAddRateCardModalNext}
                    >
                        {isLoading ? LoaderForBtn : 'Next'}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
